'use client';

import { logger } from '@/lib/logger';
import { useState } from 'react';
import { useGetUser } from '@/hooks/useAuth';
import { KYCVerification } from '@/app-components/shared/kyc-verification';
import { Clock, XCircle } from 'lucide-react';

interface KYCGuardProps {
  children: React.ReactNode;
}

export function KYCGuard({ children }: KYCGuardProps) {
  const { data: user, refetch } = useGetUser();
  const [showRejectedForm, setShowRejectedForm] = useState(false);

  const handleClose = () => {
    // Refetch user data to get latest KYC status
    refetch();
  };

  // Render different content based on KYC status
  const renderModalContent = () => {
    if (!user) return null;

    // If documents submitted and under review
    if (user.kycStatus === 'IN_REVIEW') {
      return (
        <div className='p-8'>
          <div className='bg-blue-50 border border-blue-200 rounded-2xl p-6'>
            <div className='flex flex-col items-center text-center gap-4'>
              <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center'>
                <Clock className='w-8 h-8 text-blue-600 animate-pulse' />
              </div>
              <div>
                <p className='font-bold text-lg text-blue-900'>Verification In Progress</p>
                <p className='text-sm text-blue-700 mt-2 leading-relaxed'>
                  Your documents are currently being reviewed by our team. This usually takes 24-48
                  hours.
                </p>
                <p className='text-xs text-blue-600 mt-3 font-medium'>
                  You'll receive a notification once your verification is complete.
                </p>
              </div>
              <button
                onClick={handleClose}
                className='mt-4 px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg'
              >
                Continue with Limited Access
              </button>
            </div>
          </div>
        </div>
      );
    }

    // If verification was rejected
    if (user.kycStatus === 'REJECTED') {
      // Show rejection message first, then allow resubmission
      if (!showRejectedForm) {
        return (
          <div className='p-8'>
            <div className='bg-red-50 border border-red-200 rounded-2xl p-6'>
              <div className='flex flex-col items-center text-center gap-4'>
                <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center'>
                  <XCircle className='w-8 h-8 text-red-600' />
                </div>
                <div>
                  <p className='font-bold text-lg text-red-900'>Verification Rejected</p>
                  <p className='text-sm text-red-700 mt-2 leading-relaxed'>
                    Unfortunately, we couldn't verify your documents. Please submit new documents to
                    continue.
                  </p>
                </div>
                <button
                  onClick={() => setShowRejectedForm(true)}
                  className='mt-4 px-6 py-3 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors shadow-lg'
                >
                  Submit New Documents
                </button>
              </div>
            </div>
          </div>
        );
      }
    }

    // Default: Show KYC verification form (for PENDING status or REJECTED after clicking resubmit)
    return (
      <KYCVerification
        onComplete={handleClose}
        onError={errorMessage => {
          logger.error('KYC Error:', errorMessage);
        }}
      />
    );
  };

  // If KYC is not approved, show only the KYC modal (block access to content).
  // Skipped entirely while the backend KYC feature flag is off.
  if (user && user.kycEnabled !== false && user.kycStatus !== 'APPROVED') {
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50'>
        <div className='w-full max-w-2xl bg-white border border-gray-200 rounded-3xl shadow-2xl overflow-hidden'>
          {renderModalContent()}
        </div>
      </div>
    );
  }

  // If KYC is approved, render children normally
  return <>{children}</>;
}
